import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface ProductDetailsViewStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnInnerContainer?: GridContainerProps;
    topGridItem?: GridItemProps;
    middleGridItem?: GridItemProps;
    middleGridItemHidden?: HiddenProps;
    bottomGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridItemHidden?: HiddenProps;
}

export const viewStyles: ProductDetailsViewStyles = {
    leftColumnGridItem: {
        width: [12, 12, 8, 9, 9],
        css: css`
            overflow: hidden;
            direction: ltr;
        `,
    },
    topGridItem: { width: 12 },
    middleGridItem: {
        width: [12, 12, 0, 0, 0],
        css: css`
            .pdp-quantity-wrapper {
                > ${GridOffset} {
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                null,
                                css`
                                    flex-direction: column;
                                `,
                                null,
                                null,
                            ],
                            "max",
                        )}
                }
            }
        `,
    },
    middleGridItemHidden: {
        above: "sm",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    bottomGridItem: { width: 12 },
    rightColumnGridItem: {
        width: [0, 0, 4, 3, 3],
        css: css`
            .action-content-right {
                [data-test-selector*="addProductToCart"]:before {
                    font-family: "Geriatric-Medical";
                    content: "\\e650";
                    font-size: 1.5625em;
                    padding-right: 0.5em;
                    display: inline;
                    width: auto;
                    height: auto;
                    line-height: inherit;
                    vertical-align: middle;
                }
                [data-test-selector*="productDetails_addToList"]:before {
                    font-family: "Geriatric-Medical";
                    content: "\\e64f";
                    font-size: 1.5625em;
                    padding-right: 0.5em;
                    display: inline;
                    width: auto;
                    height: auto;
                    line-height: inherit;
                    vertical-align: middle;
                }
                .price-wrap {
                    span:not(.unit-label) {
                        font-size: 20px;
                    }
                }
                label + [class*="FormInputWrapper"] {
                    margin-top: 2px;
                }
            }
            @media screen and (min-width: 1200px) {
                .action-content-right {
                    max-width: 235px;
                }
            }

            .unit-measure-section {
                width: 70%;
                float: left;
                margin-bottom: 14px;

                & + [class*="FormFieldStyle"] {
                    width: 30%;
                    float: right;
                }
            }
        `,
    },
    rightColumnGridItemHidden: {
        below: "md",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    container: {
        css: css`
            direction: rtl;

            .part-mfg-wrap {
                margin: 10px 0;
            }

            &.product-detail-page {
                .tab-button-wrapper li {
                    border: 1px solid #d5d5d5;
                    border-bottom: none;
                    margin-bottom: -1px;
                    padding: 0.5em 1.5em;
                    background-color: #fff;
                    color: #708090;
                    margin-right: 2px;
                    font-weight: 600;
                }

                .tab-button-wrapper li[aria-selected*="false"] {
                    font-weight: 600;
                    display: inline-block;
                    padding: 0.5em 1.5em;
                    list-style: none;
                    cursor: pointer;
                    float: left;
                    background-color: #708090;
                    color: #fff;
                    border-left: 1px solid #708090;
                    border-right: 1px solid #708090;
                }
                .tab-button-wrapper li[aria-selected*="true"] {
                    border-bottom: none;
                }

                .tab-button-wrapper ul {
                    padding: 0;
                    overflow: hidden;
                    background: #fff;
                    width: fit-content;
                }
                .tab-button-wrapper li[aria-selected*="false"]:hover {
                    border-bottom: none;
                }

                .tab-button-wrapper [data-id*="tabContent"] {
                    border: 1px solid #d5d5d5;
                    border-top: none;
                }

                .tab-button-wrapper [data-id*="tabContent"] > div:not([hidden]) {
                    margin-top: -3px;
                    border-top: 1px solid #cccccc;
                    border-bottom: none;
                    padding: 24px;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                .tab-button-wrapper {
                                    display: none;
                                }
                            `,
                        ],
                        "max",
                    )}

                .qty-input-wrap {
                    max-width: 70px;
                    margin-bottom: 20px;

                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                null,
                                css`
                                    margin-bottom: 0;
                                `,
                                null,
                                null,
                            ],
                            "max",
                        )}
                }

                ~ .top-sellers-wrap {
                    border: none;
                    max-width: 100%;
                    border-radius: 10px;
                    margin-bottom: 30px;
                }

                .product-info-wrapper [class*="CarouselSlidesContainer"] {
                    margin: 15px;
                    margin-left: -40px;
                }

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                ~ .top-sellers-wrap {
                                    margin-left: 0;
                                }

                                [class*="AccordionSectionHeader"] button {
                                    font-size: inherit;
                                    font-weight: 600;
                                    margin: 0;
                                    margin-top: 2px;
                                    padding: 0.5em 1.25em;
                                    background-color: #708090;
                                    color: #fff;
                                    border: 1px solid #708090;
                                }
                                [class*="AccordionSectionHeader"] button span {
                                    font-weight: 600;
                                    background-color: #708090;
                                    color: #fff;
                                }
                                [class*="AccordionSectionHeader"] button[aria-expanded="true"] {
                                    background: #fff;
                                    border: 1px solid #d5d5d5;
                                    color: #708090;
                                }
                                [class*="AccordionSectionHeader"] button[aria-expanded="true"] span {
                                    background: #fff;
                                    color: #708090;
                                }
                                .product-info-wrapper [class*="CarouselSlidesContainer"] {
                                    margin-left: 0;
                                }
                            `,
                        ],
                        "max",
                    )}

                @media screen and (max-width: 1080px) {
                    ~ .product-slider {
                        margin: 0;
                    }
                }
            }
        `,
    },
};

const styles = viewStyles;

const ProductDetailsView: React.FC<WidgetProps> = props => {
    return (
        <GridContainer className="product-detail-page" {...styles.container}>
            <GridItem {...styles.rightColumnGridItem} data-test-selector="productDetails_rightColumn">
                <Hidden className="action-content-right" {...styles.rightColumnGridItemHidden}>
                    <Zone contentId={props.id} zoneName="Content3" />
                </Hidden>
            </GridItem>
            <GridItem {...styles.leftColumnGridItem} data-test-selector="productDetails_leftColumn">
                <GridContainer {...styles.leftColumnInnerContainer}>
                    <GridItem {...styles.topGridItem}>
                        <Zone contentId={props.id} zoneName="Content0" />
                    </GridItem>
                    <GridItem {...styles.middleGridItem}>
                        <Hidden {...styles.middleGridItemHidden}>
                            <Zone contentId={props.id} zoneName="Content1" />
                        </Hidden>
                    </GridItem>
                    <GridItem {...styles.bottomGridItem}>
                        <Zone contentId={props.id} zoneName="Content2" />
                    </GridItem>
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect()(ProductDetailsView),
    definition: {
        displayName: "View",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
